import {gql} from '@apollo/client';

export const ACADEMY_COMMISSION_FIELDS = gql`
  fragment AcademyCommissionFields on AcademyCommission {
    isEnabled
    basis
    rates {
      currency
      type
      value
    }
  }
`;

export const REFERRAL_PARTNER_FIELDS = gql`
  fragment ReferralPartnerFields on ReferralPartner {
    id
    email
    firstName
    lastName
    phoneNumber
    countryCode
    referralCode
    referralPartnerType
    referralPartnerPercentage {
      currency
      volumeThreshold
      percentageCutOnFees
    }
    # One rate per currency now, not a single Float — the field is an object
    # list, so it needs a subselection or the query is rejected outright.
    referralPartnerCommissionOnNewInvestedUsers {
      currency
      amount
    }
    referralPartnerAcademyCommission {
      ...AcademyCommissionFields
    }
    referralPartnerSpreadSheetId
    createdAt
    updatedAt
  }
  ${ACADEMY_COMMISSION_FIELDS}
`;

export const GET_ALL_REFERRAL_PARTNERS = gql`
  query AdminGetReferralPartners(
    $page: Int
    $limit: Int
    $filters: AdminReferralPartnerFilters
  ) {
    adminGetReferralPartners(page: $page, limit: $limit, filters: $filters) {
      ... on AdminReferralPartnersResponse {
        page
        totalDocs
        hasPrevPage
        hasNextPage
        prevPage
        nextPage
        offset
        partners {
          id
          email
          firstName
          lastName
          referralCode
          referralPartnerType
          totalEarnings
          totalVolume
          currency
          totalReferees
          activeReferees
          totalAcademySales
          createdAt
        }
      }
      ... on Error {
        statusCode
        message
      }
    }
  }
`;

export const GET_REFERRAL_PARTNER_BY_ID = gql`
  query AdminGetReferralPartnerById($partnerId: ID!) {
    adminGetReferralPartnerById(partnerId: $partnerId) {
      ... on AdminReferralPartnerDetail {
        partner {
          ...ReferralPartnerFields
        }
        overview {
          totalEarnings
          totalVolume
          totalReferees
          activeReferees
          inactiveReferees
          avgEarningsPerReferee
          currency
          totalAcademyEarnings
          totalAcademySales
          totalAcademyVolume
        }
        recentReferees {
          id
          fullName
          email
          dateSignedUp
          hasSettledOrder
          totalInvested
          hasPurchasedAcademy
          totalAcademySpend
        }
        recentBreakdown {
          id
          period
          status
          totalCommission
          fees
          volumeGenerated
          platformFees
          commissionDueOnNewInvestedUsers
          totalNewInvestedUsers
          currency
          academySalesCount
          academyVolume
          commissionDueOnAcademySales
        }
      }
      ... on Error {
        statusCode
        message
      }
    }
  }
  ${REFERRAL_PARTNER_FIELDS}
`;

export const CREATE_REFERRAL_PARTNER = gql`
  mutation AdminCreateReferralPartner($data: AdminCreateReferralPartnerInput!) {
    adminCreateReferralPartner(data: $data) {
      ... on AdminCreateReferralPartnerResponse {
        success
        message
        partner {
          ...ReferralPartnerFields
        }
      }
      ... on Error {
        statusCode
        message
      }
    }
  }
  ${REFERRAL_PARTNER_FIELDS}
`;

export const UPDATE_REFERRAL_PARTNER = gql`
  mutation AdminUpdateReferralPartner($data: AdminUpdateReferralPartnerInput!) {
    adminUpdateReferralPartner(data: $data) {
      ... on AdminUpdateReferralPartnerResponse {
        success
        message
        partner {
          ...ReferralPartnerFields
        }
      }
      ... on Error {
        statusCode
        message
      }
    }
  }
  ${REFERRAL_PARTNER_FIELDS}
`;

export const REVOKE_REFERRAL_PARTNER = gql`
  mutation AdminDeleteReferralPartner($data: AdminDeleteReferralPartnerInput!) {
    adminDeleteReferralPartner(data: $data) {
      ... on AdminDeleteReferralPartnerResponse {
        success
        message
        partnerId
        totalReferees
        pendingEarningsCount
        pendingEarningsAmount
        couponsDisabled
      }
      ... on Error {
        statusCode
        message
      }
    }
  }
`;

export const RESET_REFERRAL_PARTNER_PASSWORD = gql`
  mutation AdminResetReferralPartnerPassword(
    $data: AdminResetReferralPartnerPasswordInput!
  ) {
    adminResetReferralPartnerPassword(data: $data) {
      ... on AdminResetReferralPartnerPasswordResponse {
        success
        message
      }
      ... on Error {
        statusCode
        message
      }
    }
  }
`;

export const EXPORT_REFERRAL_PARTNERS = gql`
  mutation AdminExportReferralPartners(
    $data: AdminExportReferralPartnersInput
  ) {
    adminExportReferralPartners(data: $data) {
      ... on AdminExportReferralPartnersResponse {
        success
        message
        jobId
        recipients
      }
      ... on Error {
        statusCode
        message
      }
    }
  }
`;

export const REWARD_SCHEMES = gql`
  query GetAllRewardSchemes {
    getAllRewardSchemes {
      ... on Error {
        statusCode
        message
      }
      ... on ResponseWithAllRewardSchemes {
        message
        data {
          rewardSchemes {
            id
            title
            language
            isActive
          }
        }
      }
    }
  }
`;

export const REFERRAL_PARTNER_STATS = gql`
  query AdminGetReferralMetrics($filters: AdminReferralPartnerFilters) {
    adminGetReferralMetrics(filters: $filters) {
      ... on AdminReferralMetrics {
        totalPartners
        totalReferees
        totalEarnings
        totalVolume
        conversionRate
        avgEarningsPerPartner
        currency
      }
      ... on Error {
        statusCode
        message
      }
    }
  }
`;
